import {Injectable} from '@angular/core';
import {BehaviorSubject, combineLatest, Observable} from 'rxjs';
import {FormBuilder} from '@angular/forms';
import {QUICK_FILTER_LIST, QuickFilterCategory} from '../model/filter';
import {GeospatialFilterStore} from './geospatial-filter.store';
import {CustomMapLayer} from '../model/layers';
import {GeospatialViewerConfiguration, GeospatialMetricsConfiguration} from '../model/viewer-config';
import {VisualizationColorCode, VisualizationType, VisualizationTypeConfiguration} from '../model/visualization';
import {GeospatialViewerControlsService} from './geospatial-viewer-controls.service';
import {UsersStore} from '@store/common/users.store';
import {filter, share, shareReplay, take, takeUntil} from 'rxjs/operators';
import {Unsubscribable} from '@core/interfaces/unsubscribable';
import {User} from '@core/interfaces/common/users';
import {StudiesStore} from '@store/common/studies.store';
import {BucketDto, MetricDto, MetricGroupDto} from '../layers/metric-layer';

@Injectable()
export class GeospatialViewerControlsStore extends Unsubscribable {
    private currentStudyId: BehaviorSubject<number> = new BehaviorSubject<number>(null);
    readonly currentStudyId$: Observable<number> = this.currentStudyId.asObservable();
    constructor(
        protected fb: FormBuilder,
        private geospatialFilterStore: GeospatialFilterStore,
        private controlsService: GeospatialViewerControlsService,
        private usersStore: UsersStore,
        private studiesStore: StudiesStore,
    ) {
        super();

        // Geospatial configuration is fetched without a workflowItemId
        this.controlsService
            .getGeospatialViewerConfiguration()
            .pipe(
                filter((geospatialViewerConfig) => !!geospatialViewerConfig),
                take(1),
            )
            .subscribe((geospatialViewerConfig) => {
                this.setConfig(geospatialViewerConfig);

                if (this.initialConfig.value === null) {
                    this.initialConfig.next(geospatialViewerConfig);
                }
            });

        // Metric configuration requires a workflowItemId
        this.studiesStore.activeStudyIdRisk$.pipe(takeUntil(this.unsubscribe$)).subscribe((studyId: number) => {
            this.currentStudyId.next(studyId);

            this.controlsService
                .getMetricConfig(studyId)
                .pipe(
                    filter((metricConfig: GeospatialMetricsConfiguration) => !!metricConfig),
                    take(1),
                )
                .subscribe((metricConfig: GeospatialMetricsConfiguration) => {
                    this.setMetricConfig(metricConfig);

                    this.refreshControls();
                });
        });

        this.usersStore.currentUser$
            .pipe(
                filter((u: User) => u && !!u),
                takeUntil(this.unsubscribe$),
            )
            .subscribe((user) => {
                this.currentUserId.next(user.id);
            });
    }

    private initialConfig: BehaviorSubject<GeospatialViewerConfiguration> =
        new BehaviorSubject<GeospatialViewerConfiguration>(null);
    readonly initialConfig$: Observable<GeospatialViewerConfiguration> = this.initialConfig.asObservable();
    private config: BehaviorSubject<GeospatialViewerConfiguration> = new BehaviorSubject<GeospatialViewerConfiguration>(
        null,
    );
    readonly config$: Observable<GeospatialViewerConfiguration> = this.config
        .asObservable()
        .pipe(share(), shareReplay(1));

    private metricConfig: BehaviorSubject<GeospatialMetricsConfiguration> =
        new BehaviorSubject<GeospatialMetricsConfiguration>(null);
    readonly metricConfig$: Observable<GeospatialMetricsConfiguration> = this.metricConfig
        .asObservable()
        .pipe(share(), shareReplay(1));

    setMetricConfig(newConfig: GeospatialMetricsConfiguration) {
        newConfig.configs.forEach(
            (config) =>
                (config.metricGroups = config.metricGroups.sort((a, b) =>
                    a.label < b.label ? -1 : a.label > b.label ? 1 : 0,
                )),
        );
        this.metricConfig.next(newConfig);
    }

    getConfig(): GeospatialViewerConfiguration {
        return this.config.value;
    }

    setConfig(newConfig: GeospatialViewerConfiguration) {
        this.config.next(newConfig);
    }

    /*
     * UI elements
     */
    // Control Panel > Measures; map controls > measure/metric
    public currentUserId: BehaviorSubject<string> = new BehaviorSubject<string>(null);
    public selectedMetricGroup: BehaviorSubject<MetricGroupDto> = new BehaviorSubject<MetricGroupDto>(null);
    readonly selectedMetricGroup$: Observable<MetricGroupDto> = this.selectedMetricGroup.asObservable();
    public selectedMetric: BehaviorSubject<MetricDto> = new BehaviorSubject<MetricDto>(null);
    // Control panel > View; map controls > visualization type
    private selectedVisualizationType = new BehaviorSubject(null);
    // Control panel > Filter > quick filters; map control > quick filters
    private quickFilters: BehaviorSubject<QuickFilterCategory[]> = new BehaviorSubject<QuickFilterCategory[]>(null);

    // Control panel > Project > visualization
    private projectLayerActive: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
    readonly projectLayerActive$: Observable<boolean> = this.projectLayerActive.asObservable();
    public projectColorCodeType: BehaviorSubject<VisualizationColorCode> = new BehaviorSubject<VisualizationColorCode>(
        VisualizationColorCode.OWNERSHIP,
    );
    public projectCategories: BehaviorSubject<BucketDto[]> = new BehaviorSubject<BucketDto[]>(null);
    // Control panel > View > additional map layers
    private CustomMapLayers: BehaviorSubject<CustomMapLayer[]> = new BehaviorSubject<CustomMapLayer[]>(null);

    public projectInEditMode: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public readyToCreateProject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false); // default value should be false

    // Controls for configuration
    public updateMapViewWindow(updateObj: any): boolean {
        const config: GeospatialViewerConfiguration = this.config.getValue();
        const newConfig = {
            ...config,
            centerCoordinate: {
                ...config.centerCoordinate,
                ...updateObj,
            },
        };

        if (!this.isEquivalent(config.centerCoordinate, newConfig.centerCoordinate)) {
            this.setConfig(newConfig);
            return true;
        }
        return false;
    }

    /*
     * External functions for managing controls
     */
    public refreshControls() {
        if (this.initialConfig.value && this.metricConfig.value) {
            const metricConfig = this.metricConfig.value;

            const activeMeasure: MetricGroupDto = metricConfig.configs[0].metricGroups[0]; //this.getDefaultMeasure(config.measures);
            const activeMetric: MetricDto = activeMeasure.metrics[0]; // this.getDefaultMetric(activeMeasure);
            const activeVisualizationType: VisualizationType = VisualizationType.INDIVIDUAL;

            this.selectedMetricGroup.next(activeMeasure);
            this.selectedMetric.next(activeMetric);
            this.selectedVisualizationType.next(activeVisualizationType);
            // Reset filters
            this.resetQuickFilters();
            this.geospatialFilterStore.resetGeospatialFilters();

            this.resetMapLayers();
        }
    }

    public resetQuickFilters() {
        this.quickFilters.next(QUICK_FILTER_LIST);
    }

    private resetMapLayers() {
        if (this.initialConfig.value) {
            const initConfig: CustomMapLayer[] = this.initialConfig.value.customLayers;
            initConfig?.forEach((layer: CustomMapLayer) => {
                if (layer.selected === undefined) {
                    layer.selected = false;
                }
            });
            this.CustomMapLayers.next(initConfig);
        }
    }

    getCurrentStudyId(): number {
        return this.currentStudyId.getValue();
    }

    // Update for project layer
    public updateProjectLayerActive(val: boolean) {
        if (this.projectLayerActive.value !== val) {
            this.projectLayerActive.next(val);
        }
    }
    // Helper method - retrieves metricDto by key
    public getMetricDtoByType(metricType: string): MetricDto {
        const metricConfig: GeospatialMetricsConfiguration = this.metricConfig.getValue();
        const allMetrics: MetricDto[] = metricConfig.configs
            .map((config) => config.metricGroups)
            .reduce((acc, groups) => acc.concat(groups), [])
            .map((group) => group.metrics)
            .reduce((acc, metrics) => acc.concat(metrics), []);
        return allMetrics.find((metric) => metric.key === metricType);
    }

    // Helper
    private isEquivalent(val1: any, val2: any): boolean {
        if (val1 instanceof Object && val2 instanceof Object) {
            const val1props = Object.keys(val1);
            const val2props = Object.keys(val2);

            if (val1props.length !== val2props.length) return false;

            for (let i = 0; i < val1props.length; i++) {
                if (val1props[i] === 'enabled') continue;
                const val2Prop = val2props.find((item) => item === val1props[i]);
                if (!val2Prop) return false;

                if (!this.isEquivalent(val1[val2Prop], val2[val2Prop])) return false;
            }
            return true;
        }
        return val1 === val2;
    }
}
